/*
GPS DataViz

Copyright © 2021 GPSDataViz

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/
import React, { useState } from "react";
import { trainingAPI } from "@api/TrainingAPI";
import { Row, Col } from "antd";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";

import "@styles/styles-TeamData.css";
import "@styles/styles-PostData.css";
import { useSelector, useDispatch } from "react-redux";
import {
    setCognitionStatDefaults,
    setTeamPresets,
} from "@store/actions/team.actions.js";
import { CompactStatTile } from "@components/StatTileV2/CompactStatTile.jsx";
import CognitionBellCurveChart from "@components/Cognition/CognitionBellCurveChart";
import TeamCognitionBellCurveChart from "@components/Cognition/TeamCognitionBellCurveChart";
import { useMetricDropdowns } from "@hooks/useDropdownInitialization.js";
import { useAuth0 } from "@auth0/auth0-react";
import { PresetSelection, PresetType } from "@components/PresetSelection.tsx";

function Cognition({ type, handleComparisonOptionChange, ...props }: {
    type: any,
    handleComparisonOptionChange: any,
    position_filter?: any,
    dataColorUtil?: any,
    showAll?: any,
    data?: any,
    movement_sig_chart_mapping?: any,
    team_name?: any,
    fp_change_metric?: any,
}) {

    const dispatch = useDispatch();
    const PRESET_TYPE = PresetType.Cognition;
    const DEFAULT_PRESET = 'Default';

    const {
        cognitionStatDefaults,
        dropdowns: { cognition: cognitionDropdowns },
        misc: { comparison_types },
        filter: { player_group, session },
        presets,
        allPlayers,
    } = useSelector(({ team }: any) => team);
    const {
        filter: { endDate, startDate, player, comparisonOption },
    } = useSelector((store) => store[type]);
    const { user } = useAuth0();

    const [statMetrics, setStatMetrics] = useState([]);
    const [currentSelectedTilePreset, setCurrentSelectedTilePreset] = useState(DEFAULT_PRESET);
    const [unsavedChanges, setUnsavedChanges] = useState(false);

    useMetricDropdowns(['cognition']);

    if (type !== "team" && type !== "player") return null;

    let playerDisplayName = "";
    if (type === "player") {
        const singlePlayer = allPlayers?.find((x: any) => x.player_id === player);
        if (singlePlayer) {
            const first = singlePlayer.first_name ?? "";
            const last = singlePlayer.last_name ?? "";
            playerDisplayName = `${first} ${last}`.trim();
        }
    }

    const handleSaveMetrics = async () => {
        const filtered = statMetrics.filter((val) => !!val);

        try {
            if (currentSelectedTilePreset === DEFAULT_PRESET) {
                await trainingAPI.updateDefaultStats(filtered, "cognition");
                dispatch(setCognitionStatDefaults(filtered));
            } else {
                const presetModified = presets?.find((preset) => preset.name === currentSelectedTilePreset);
                await trainingAPI.updatePreset({
                    name: currentSelectedTilePreset, type: PRESET_TYPE, config: filtered, preset_id: presetModified.preset_id
                });
                presetModified.config = filtered;
                dispatch(setTeamPresets(presets));
            }
            message.success("Saved!");
        } catch (e) {
            console.log(e);
            message.error("Error. Unable to edit ");
        }
    };

    const handleChangeMetric = (index) => (key) => {
        setStatMetrics((prevStatMetrics) => {
            const newStatMetrics = [...prevStatMetrics];
            newStatMetrics[index] = key;
            return newStatMetrics;
        });
        props.fp_change_metric?.(index, key);
        setUnsavedChanges(true);
    };

    return (
        <>
            <div
                className="flex flex-col justify-center items-center w-full"
                id="statistics-widget"
            >
                <Row
                    gutter={16}
                    style={{ width: "80%", marginBottom: 16, justifyContent: 'center', gap: '10px' }}>
                    <PresetSelection
                        presets={presets}
                        user={user}
                        presetType={PRESET_TYPE}
                        statDefaults={cognitionStatDefaults}
                        currStatMetrics={statMetrics}
                        setStatMetrics={setStatMetrics}
                        currentSelectedTilePreset={currentSelectedTilePreset}
                        setCurrentSelectedTilePreset={setCurrentSelectedTilePreset}
                        comparisonTypes={comparison_types}
                        comparisonOption={comparisonOption}
                        setComparisonOption={handleComparisonOptionChange}
                        handleSave={handleSaveMetrics}
                        warnUnsavedChanges={unsavedChanges}
                        setWarnUnsavedChages={setUnsavedChanges}
                    />
                </Row>
                <Row
                    gutter={16}
                    style={{ width: "90%", marginBottom: 16 }}
                    className="exportable tab-testing testing-stats"
                >
                    {statMetrics?.map((metric, i) => {
                        return (
                            <Col span={6} key={i}>
                                <div
                                    key={i}
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}
                                >
                                    <CompactStatTile
                                        dropdowns={cognitionDropdowns}
                                        metric_identifier_with_stat={metric}
                                        onChange={handleChangeMetric(i)}
                                        option={comparisonOption}
                                        endDate={endDate}
                                        startDate={startDate}
                                        isCompact={true}
                                        type={type}
                                        comparison={comparisonOption}
                                        {...(type === "player" && {
                                            playerId: player,
                                        })}
                                        positionFilter={props.position_filter}
                                    />
                                </div>
                            </Col>
                        );
                    })}
                </Row>
            </div>

            {type === "player" ? (
                <div style={{ display: "flex", justifyContent: "center" }}>
                    <div style={{ width: "90%" }}>
                        <CognitionBellCurveChart
                            statMetrics={statMetrics as any[]}
                            startDate={startDate}
                            endDate={endDate}
                            playerId={player}
                            position={props.position_filter}
                            player_group={player_group}
                            session={session}
                            playerName={playerDisplayName}
                        />
                    </div>
                </div>
            ) : (
                <TeamCognitionBellCurveChart
                    statMetrics={statMetrics as any[]}
                    startDate={startDate}
                    endDate={endDate}
                />
            )}

        </>
    );
}

export default Cognition;
